// Shapes the menu API responses into the rows and detail sections the native
// Menu module renders. Field keys mirror the API exactly, typos included
// (`filedName`), so nothing is guessed at read time.

export const NA = "N/A"

export const MEAL_PERIOD = {
  BAR: "Bar Menu",
  SPIRITS: "Spirits",
  WINE_BY_GLASS: "Wine By Glass",
  WINE_BY_BOTTLE: "Wine By Bottle"
}

export const ITEM_KIND = {
  FOOD: "food",
  WINE: "wine",
  BAR: "bar",
  SPIRIT: "spirit"
}

// The server stamps item_type "VIP" on items of the VIP meal period
export function isVipItemType(itemType) {
  return String(itemType ?? "").trim().toUpperCase() === "VIP"
}

// Which listing endpoint and row a meal period maps to.
export function kindForMealPeriod(mealPeriodName) {
  switch (mealPeriodName) {
    case MEAL_PERIOD.BAR:
      return ITEM_KIND.BAR
    case MEAL_PERIOD.SPIRITS:
      return ITEM_KIND.SPIRIT
    case MEAL_PERIOD.WINE_BY_GLASS:
    case MEAL_PERIOD.WINE_BY_BOTTLE:
      return ITEM_KIND.WINE
    default:
      return ITEM_KIND.FOOD
  }
}

// Bar and Spirits carry their own category list; everything else uses food_list.
export function usesDynamicCategories(mealPeriodName) {
  return mealPeriodName === MEAL_PERIOD.BAR || mealPeriodName === MEAL_PERIOD.SPIRITS
}

// Allergens first, then the diets (backend DIET_GROUPS); `id` is the snapshot's group name.
export const ALLERGEN_FILTERS = [
  { id: "Dairy", title: "Dairy", icon: "milk" },
  { id: "Eggs", title: "Eggs", icon: "eggs" },
  { id: "Fish", title: "Fish", icon: "fish" },
  { id: "Peanuts", title: "Peanut", icon: "peanut" },
  { id: "Crustacean Shellfish", title: "Shellfish", icon: "crab" },
  { id: "Gluten", title: "Gluten", icon: "wheat" },
  { id: "Soybeans", title: "Soybean", icon: "soyabean" },
  { id: "Sesame", title: "Sesame", icon: "sesame" },
  { id: "Tree Nuts", title: "Tree Nuts", icon: "nuts" },
  { id: "Pork", title: "Pork", icon: "sausage" },
  { id: "Allium", title: "Allium", icon: "onion" },
  { id: "Vegan", title: "Vegan", icon: "vegan" },
  { id: "Vegetarian", title: "Vegetarian", icon: "vegetarian" }
]

export function formatPrice(value) {
  const price = Number(value)
  if (!Number.isFinite(price) || price <= 0) return ""
  return `$${Math.round(price)}`
}

// Trims any scalar the API returns — dynamic fields keep the JSON type they were
// stored as, so a VIP table arrives as a number as often as a string.
export function displayText(value) {
  if (value === null || value === undefined || typeof value === "object") return ""
  return String(value).trim()
}

// A dynamic field value is either a plain scalar or { name, measurement }.
function readValue(entry) {
  if (!entry || typeof entry !== "object") return displayText(entry)
  const name = displayText(entry.name ?? entry.fact)
  if (!name) return ""
  const measurement = displayText(entry.measurement)
  return measurement ? `${name} (${measurement})` : name
}

// data_to_display[].field_type of a menu field drawn as its own card after Details
export const MENU_FIELD_TYPE_CARDS = "Cards"

export function normalizeFields(dataToDisplay) {
  if (!Array.isArray(dataToDisplay)) return []
  return dataToDisplay
    .map((field) => {
      const entries = Array.isArray(field?.value) ? field.value : [field?.value]
      const row = {
        name: field?.filedName ?? field?.fieldName ?? "",
        values: entries.map(readValue).filter(Boolean),
        showBullets: Boolean(field?.show_bullets),
        canBeExpanded: Boolean(field?.can_be_expanded),
        // No field_type (an older backend, or a row that isn't a menu field) stays a Details row
        isCards: field?.field_type === MENU_FIELD_TYPE_CARDS
      }
      return row.isCards ? { ...row, cards: cardFieldCards(entries) } : row
    })
    .filter((field) =>
      field.isCards ? field.cards.length > 0 : field.name && field.values.length
    )
}

// Details rows, and the Cards fields drawn after them as { heading, cards } sections
export function splitCardFields(fields) {
  return {
    fields: fields.filter((field) => !field.isCards),
    cardFields: fields
      .filter((field) => field.isCards)
      .map((field) => ({ heading: field.name, cards: field.cards }))
  }
}

// One card per value entry; a plain-text entry (no title or description) shows its name
function cardFieldCards(entries) {
  return entries
    .map((entry) => {
      const card = readDidYouKnowCard(entry)
      return hasCardText(card) ? card : { ...card, description: readValue(entry) }
    })
    .filter(hasCardText)
}

// First field whose name matches, compared case- and punctuation-insensitively.
export function fieldValues(fields, names) {
  const wanted = new Set(names.map(normalizeName))
  const match = fields.find((field) => wanted.has(normalizeName(field.name)))
  return match?.values ?? []
}

export function fieldText(fields, names, fallback = NA) {
  const values = fieldValues(fields, names)
  return values.length ? values.join(", ") : fallback
}

function normalizeName(name) {
  return String(name).trim().toLowerCase().replace(/[():]/g, "")
}

export function normalizeHistory(itemHistory) {
  const title = displayText(itemHistory?.title)
  const description = displayText(itemHistory?.description)
  if (!title && !description) return null
  return { title, description }
}

export function normalizeDidYouKnow(list) {
  if (!Array.isArray(list)) return []
  return list.map(readDidYouKnowCard).filter(hasCardText)
}

// The did_you_know card shape, shared by the item's own cards and Cards field cards
function readDidYouKnowCard(entry) {
  return {
    title: displayText(entry?.title),
    description: displayText(entry?.description),
    imageUrl: entry?.image?.url ?? "",
    thumbnailUrl: entry?.image?.thumbnail_url ?? "",
    displayUrl: entry?.image?.display_url ?? ""
  }
}

function hasCardText(card) {
  return Boolean(card.title || card.description)
}

// Port of HistoryIcon(title:) — the local category icon the detail header falls back to.
export function categoryIcon(label) {
  switch (String(label ?? "").trim().toLowerCase()) {
    case "cocktail":
    case "cocktails":
    case "beverage":
    case "bar menu":
      return "cocktails"
    case "food":
    case "dinner":
    case "lunch":
    case "brunch":
      return "food"
    case "vip":
      return "vip"
    case "wine region":
      return "wine-region"
    case "wine":
    case "spirits":
    case "spirit":
      return "wine"
    case "menu item":
      return "menu-item"
    case "service":
      return "service"
    case "retail":
    case "restaurants":
    case "spa":
    case "rooms":
    case "wellness":
    case "experiences":
    case "hotel":
    case "hotels":
      return "hotel"
    default:
      return "special"
  }
}

// The detail header's small label is selected_item_type with underscores spaced out.
function typeLabel(raw) {
  const type = String(raw?.item_type ?? "").replace(/_/g, " ").trim()
  if (type) return type
  return String(raw?.category_tag ?? "").trim()
}

function baseItem(raw, kind) {
  const { fields, cardFields } = splitCardFields(normalizeFields(raw?.data_to_display))
  const name = displayText(raw?.name)
  const headline = displayText(raw?.menu_description)
  return {
    kind,
    id: raw?.id ?? "",
    name,
    price: formatPrice(raw?.price),
    imageUrl: raw?.image_url ?? "",
    thumbnailUrl: raw?.image_thumbnail_url ?? "",
    displayUrl: raw?.image_display_url ?? "",
    video: raw?.video ?? "",
    typeLabel: typeLabel(raw),
    soundbiteUrl: raw?.soundbite_url ?? "",
    headline,
    fields,
    cardFields,
    itemHistory: normalizeHistory(raw?.item_history),
    didYouKnow: normalizeDidYouKnow(raw?.did_you_know)
  }
}

export function normalizeFoodItem(raw) {
  const item = baseItem(raw, ITEM_KIND.FOOD)
  const dynamicDescription = fieldText(item.fields, ["Menu Description"], "")
  const description = dynamicDescription || item.headline
  return {
    ...item,
    headline: description,
    // The row leads with the menu description, from the dynamic field when the
    // restaurant defines one and from the column otherwise.
    description,
    allergens: fieldText(item.fields, ["Allergens"]),
    hasChefDescription: fieldValues(item.fields, ["Chef's Description"]).length > 0,
    itemType: raw?.item_type ?? ""
  }
}

export function normalizeWineItem(raw) {
  const item = baseItem(raw, ITEM_KIND.WINE)
  const region = fieldText(item.fields, ["Region"], "")
  const country = fieldText(item.fields, ["Country"], "")
  return {
    ...item,
    origin: [region, country].filter(Boolean).join(", ") || NA,
    varietals: fieldText(item.fields, ["Varietal(s)", "Varietals"])
  }
}

const BEER_CATEGORY = "Beer"

export function normalizeBarItem(raw, categoryName) {
  const item = baseItem(raw, ITEM_KIND.BAR)
  const isBeer = categoryName === BEER_CATEGORY
  // Beer swaps the three summary lines for its own set, matching the native cell.
  return {
    ...item,
    lines: isBeer
      ? [
          { label: "Style: ", value: fieldText(item.fields, ["Style"]) },
          { label: "ABV: ", value: fieldText(item.fields, ["ABV"]) },
          { label: "Brewery: ", value: fieldText(item.fields, ["Brewery"]) }
        ]
      : [
          { label: "Glassware: ", value: fieldText(item.fields, ["Glassware"]) },
          { label: "Garnish: ", value: fieldText(item.fields, ["Garnish"]) },
          { label: "Ingredients: ", value: fieldText(item.fields, ["Ingredients"]) }
        ]
  }
}

export function normalizeSpiritItem(raw) {
  const name = displayText(raw?.name)
  const headline = displayText(raw?.description)
  const fields = spiritFields(raw)
  return {
    kind: ITEM_KIND.SPIRIT,
    id: raw?.id ?? "",
    name,
    price: formatPrice(raw?.item_price ?? raw?.price),
    imageUrl: raw?.image_url ?? "",
    thumbnailUrl: raw?.image_thumbnail_url ?? "",
    displayUrl: raw?.image_display_url ?? "",
    video: "",
    typeLabel: typeLabel(raw) || "SPIRIT",
    soundbiteUrl: raw?.soundbite_url ?? "",
    headline,
    description: headline || NA,
    region: displayText(raw?.origin) || NA,
    fields,
    cardFields: [],
    itemHistory: normalizeHistory(raw?.item_history),
    didYouKnow: normalizeDidYouKnow(raw?.did_you_know)
  }
}

// Spirits are flat columns rather than dynamic fields — lay them out in the
// order the native detail screen stacks them.
function spiritFields(raw) {
  const uniqueFacts = (Array.isArray(raw?.unique_facts) ? raw.unique_facts : [])
    .map((entry) => displayText(entry?.fact))
    .filter(Boolean)
  const ingredients = (Array.isArray(raw?.ingredient) ? raw.ingredient : [])
    .map(readValue)
    .filter(Boolean)

  return [
    { name: "Description", values: [raw?.description] },
    { name: "Region", values: [raw?.origin] },
    { name: "Unique Facts", values: uniqueFacts, showBullets: true },
    { name: "Ingredients", values: ingredients },
    { name: "Nose", values: [raw?.nose] },
    { name: "Palate", values: [raw?.palate] },
    { name: "Finish", values: [raw?.finish] },
    { name: "Distillation", values: [raw?.distillation] },
    { name: "Aging", values: [raw?.aging] }
  ]
    .map((field) => ({
      name: field.name,
      values: field.values.map(displayText).filter(Boolean),
      showBullets: Boolean(field.showBullets),
      canBeExpanded: false
    }))
    .filter((field) => field.values.length)
}

// Bar and Spirits categories share one serializer.
export function normalizeCategory(raw) {
  return {
    id: raw?.id ?? "",
    name: displayText(raw?.name),
    description: displayText(raw?.description),
    imageUrl: raw?.image_url ?? "",
    thumbnailUrl: raw?.image_thumbnail_url ?? ""
  }
}

export function normalizeMealPeriod(raw) {
  return {
    id: raw?.meal_type_id ?? "",
    name: displayText(raw?.meal_type),
    hasAllergens: Boolean(raw?.has_allergens),
    categories: (Array.isArray(raw?.food_list) ? raw.food_list : []).map((category) => ({
      id: category?.id ?? "",
      name: displayText(category?.name)
    }))
  }
}
